import { ref, computed } from "vue";
import { nanoid } from "nanoid";
import type { ProtocolExport } from "@/automation/protocolExport";
import type { LabwareObject } from "@/automation/labware/labwareManager"; // Import directly from source
// Import the base export function and necessary types
import { exportProtocolBase } from "@/automation/protocolExport";
import type { EditorState } from "@tiptap/pm/state";
import type { SystemConfig } from "@/state/configState"; // Corrected import path
import { useConfigState } from "@/state/configState";
import { isTipLabware } from "@/automation/utils/labwareClassification";
// Import the decoration functions from the new utility file
import {
  decorateProtocolWithTips,
  decorateProtocolWithHiddenLabware,
  decorateParametersWithReplicates,
  decorateProtocolWithDefaultLabwareMetadata,
  decorateStepsWithRequiredTipType,
  decorateProtocolWithLabwareConfig,
  syncTipReturn,
} from "@/automation/exportUtils";

// --- Module-scope (singleton) state --------------------------------------
// Reactive reference to the complete protocol data
const protocol = ref<ProtocolExport | null>(null);

// --- Actions --------------------------------------------------------------

/** Initializes the protocol state by running the base export and layering tip assignments. */
function initExportProtocol(
  state: EditorState,
  activeConfig: SystemConfig,
  metadata?: {
    methodId: string;
    workspaceId?: string;
    userId?: string;
  },
  selectedHeadId?: string | null
) {
  const baseProtocol = exportProtocolBase(
    state,
    activeConfig,
    metadata,
    selectedHeadId
  ) as ProtocolExport;

  // Decorate with tip labware and parameters using the imported function
  const protocolWithReplicates = decorateParametersWithReplicates(
    baseProtocol,
    activeConfig
  );

  // Decorate with tip labware and parameters using the imported function
  const protocolWithTips = decorateProtocolWithTips(
    protocolWithReplicates,
    state,
    activeConfig
  );

  // Resolve requiredTipTypeIds BEFORE hidden labware so trash/tip_return
  // can use the volume-resolved tip type (not just config options[0])
  const protocolWithRequiredTipType = decorateStepsWithRequiredTipType(
    protocolWithTips,
    activeConfig
  );

  // Auto-fill all hidden labware params (adapters, waste, trash, bioshake, etc.)
  const protocolWithHiddenLabware = decorateProtocolWithHiddenLabware(
    protocolWithRequiredTipType,
    activeConfig
  );

  const protocolWithDefaultMetadata =
    decorateProtocolWithDefaultLabwareMetadata(protocolWithHiddenLabware, activeConfig);

  // Attach labware config, liquid class data, and selected head for stateless runner
  const protocolWithLabwareConfig = decorateProtocolWithLabwareConfig(
    protocolWithDefaultMetadata,
    activeConfig,
    selectedHeadId
  );

  // Derive tip_return params from the default trash choices so the protocol is
  // in a consistent state before the operator touches the tip-allocation screen.
  syncTipReturn(protocolWithLabwareConfig, activeConfig);

  setProtocol(protocolWithLabwareConfig);
}

/** Set the protocol data for the composable to manage */
function setProtocol(newProtocol: ProtocolExport | null) {
  protocol.value = newProtocol;
}

/** Create a new tip labware instance and add it to the protocol */
function createTipLabware(typeId: string, label: string): LabwareObject | null {
  if (!protocol.value) {
    console.error("Cannot create tip labware: protocol not set.");
    return null;
  }
  const id = `user-tip-${nanoid(6)}`;
  // Ensure LabwareObject type includes properties if needed by the protocol structure
  const lw: LabwareObject = { id, typeId, label };

  // Ensure labware array exists before pushing
  if (!protocol.value.labware) {
    protocol.value.labware = [];
  }
  protocol.value.labware.push(lw);

  return lw;
}

/** Remove a tip labware instance from the protocol and unassign it from any steps that reference it.
 *  Returns the IDs of steps whose tip assignment was cleared so the caller can reassign them. */
function removeTipLabware(tipId: string): string[] {
  if (!protocol.value) {
    console.error("Cannot remove tip labware: protocol not set.");
    return [];
  }

  const labwareIndex = protocol.value.labware.findIndex(
    (lw) => lw.id === tipId
  );
  if (labwareIndex === -1) return [];

  // Remove from the protocol labware list
  protocol.value.labware.splice(labwareIndex, 1);

  // Unassign from every step that references this tip
  const affectedStepKeys: string[] = [];
  for (const step of protocol.value.steps) {
    const tipsParam = step.parameters.find((p) => p.name === "Tips");
    if (!tipsParam) continue;

    const val = tipsParam.value as { type: string; labware?: { id: string } };
    if (val?.type === "labware" && val.labware?.id === tipId) {
      // Clear the assignment – the UI will show the first available option
      tipsParam.value = { type: "labware", labware: { id: "", typeId: "", label: "" } };
      affectedStepKeys.push(step.uid || step.id);
    }
  }

  return affectedStepKeys;
}

/** Reset the protocol state */
function resetProtocolState() {
  protocol.value = null;
}

// --- Computed Properties -------------------------------------------------

// All available labware suitable for tips
const allTipLabware = computed((): LabwareObject[] => {
  if (!protocol.value?.labware) return []; // Add safe navigation for labware array

  // Get config lazily (inside computed, not at module scope) to avoid
  // calling useConfigState() before VueFire plugin is initialized.
  const { activeConfig } = useConfigState();

  // Refined filter: include specific IDs or config-based tip classification
  return protocol.value.labware.filter(
    (lw) =>
      lw.id.startsWith("tips-") || // Auto-generated standard tips
      lw.id.startsWith("user-tip-") || // User-created tips
      isTipLabware(lw.typeId, activeConfig.value) // Config-based tip classification
  );
});

// True when every step that requires tips has a valid (non-empty) tip assigned
const allStepTipsAssigned = computed((): boolean => {
  if (!protocol.value?.steps) return true; // No steps = nothing to validate

  for (const step of protocol.value.steps) {
    const tipsParam = step.parameters.find((p) => p.name === "Tips");
    if (!tipsParam) continue; // Step doesn't need tips

    const val = tipsParam.value as
      | { type: string; labware?: { id: string } }
      | undefined;

    if (
      !val ||
      val.type !== "labware" ||
      !val.labware?.id ||
      val.labware.id === ""
    ) {
      return false; // This step is missing a tip assignment
    }
  }
  return true;
});

// --- Composable -----------------------------------------------------------
// Rename the exported function
export function useExportProtocol() {
  return {
    protocol,
    setProtocol, // Expose if needed for direct manipulation elsewhere
    createTipLabware,
    removeTipLabware,
    resetProtocolState,
    allTipLabware,
    allStepTipsAssigned,
    initExportProtocol, // This function now uses the imported decorators
  };
}
